import { useRef } from 'react'
import { BracesIcon } from 'lucide-react'
import type { NotificationTemplateVariable } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'

export function insertTemplateVariable(
  value: string,
  variableName: string,
  selectionStart: number,
  selectionEnd: number
): { value: string; cursor: number } {
  const token = `{{${variableName}}}`
  const start = Math.max(0, Math.min(selectionStart, value.length))
  const end = Math.max(start, Math.min(selectionEnd, value.length))
  return {
    value: `${value.slice(0, start)}${token}${value.slice(end)}`,
    cursor: start + token.length
  }
}

export function MessageTemplateField({
  id,
  label,
  value,
  onChange,
  variables,
  hint,
  placeholder,
  multiline = false,
  error
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  variables: NotificationTemplateVariable[]
  hint: string
  placeholder: string
  multiline?: boolean
  error?: string
}) {
  const controlRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const selectionRef = useRef({ start: value.length, end: value.length })

  const rememberSelection = () => {
    const control = controlRef.current
    if (!control) return
    selectionRef.current = {
      start: control.selectionStart ?? value.length,
      end: control.selectionEnd ?? value.length
    }
  }

  const insertVariable = (name: string) => {
    const next = insertTemplateVariable(
      value,
      name,
      selectionRef.current.start,
      selectionRef.current.end
    )
    onChange(next.value)
    selectionRef.current = { start: next.cursor, end: next.cursor }
    requestAnimationFrame(() => {
      controlRef.current?.focus()
      controlRef.current?.setSelectionRange(next.cursor, next.cursor)
    })
  }

  const common = {
    id,
    value,
    placeholder,
    'aria-invalid': error ? (true as const) : undefined,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value)
      selectionRef.current = {
        start: event.target.selectionStart ?? event.target.value.length,
        end: event.target.selectionEnd ?? event.target.value.length
      }
    },
    onSelect: rememberSelection,
    onKeyUp: rememberSelection,
    onClick: rememberSelection
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={variables.length === 0}
              aria-label={`Insert variable into ${label}`}
            >
              <BracesIcon className="size-3.5" />
              Insert variable
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Message variables</DropdownMenuLabel>
            {variables.map((variable) => (
              <DropdownMenuItem key={variable.name} onSelect={() => insertVariable(variable.name)}>
                <span className="flex min-w-0 flex-col">
                  <code className="text-xs">{`{{${variable.name}}}`}</code>
                  <span className="text-xs text-ink-4">{variable.description}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {multiline ? (
        <Textarea {...common} ref={controlRef as React.Ref<HTMLTextAreaElement>} rows={3} />
      ) : (
        <Input {...common} ref={controlRef as React.Ref<HTMLInputElement>} />
      )}
      <p className="m-0 text-xs text-ink-3">{hint}</p>
      {error && <p className="m-0 text-xs text-st-down">{error}</p>}
    </div>
  )
}
